﻿import React from 'react';

import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartTooltip, ChartXAxis, ChartXAxisItem, ChartYAxis, ChartYAxisItem } from '@progress/kendo-react-charts';
import { SeriesType } from '@progress/kendo-react-charts/dist/npm/field-types/series-type';
import { Col, Container } from 'react-bootstrap';
import { ReportComponent } from 'apis/ReportApi';
import { FormattedMessage } from 'react-intl';

import 'pages/reports/components/ChartGeneric/styles/chart-generic.scss';

interface ChartGenericProps {
	centerTitle?: string;
	chartLegendVisible?: boolean;
	component: ReportComponent;
	index: number;
	isSubReport?: boolean;
	loading: boolean;
}

export const ChartGeneric = ({
    centerTitle,
    chartLegendVisible = false,
    component,
    index,
    isSubReport = false,
    loading
}: ChartGenericProps) => {

    const renderTooltip = (context: any) => {

        const { dataItem, title, value } = context.point || context;
        let hoverTitle = title;
        if (!hoverTitle) {

            hoverTitle = dataItem.HoverText;

        }
        return (
            <div>
                {hoverTitle ? hoverTitle : (
                    <>
                        {value || value.xField}
                        %
                    </>
                )}
            </div>
        );

    };

    const centerRenderer = () => {

        if (centerTitle && component.Results) {

            let total = 0;
            component.Results.SeriesList.forEach(dataList => dataList.Data.map(item => {

                total += parseInt(item.Share);

            }));
            return (
                <div>
                    <b>
                        {total}
                    </b>
                    <div>
                        {centerTitle}
                    </div>
                </div>
            );

        } else {

            return (
                <div>
                    <div>
                        {centerTitle}
                    </div>
                </div>
            );

        }

    };

    const generateSkeletonChart = (component: ReportComponent) => {

        switch (component.Type) {

            case 'donut':
                return (
                    <div className="skeleton-pie skeleton">
                        <div className="inside-circle"></div>
                    </div>
                );
            case 'bar':
            case 'column':
            default:
                return (
                    <div className={`${component.Type === 'bar' ? 'bar-chart' : ''} skeleton-column-container`}>
                        <div className={`${component.Type === 'bar' ? 'bar-ticks' : ''} skeleton-column-numbers`}>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                        </div>
                        <div className="skeleton skeleton-column">
                            <div className="column-fifty"></div>
                            <div></div>
                            <div className="column-sixty-five"></div>
                            <div className="column-fifty"></div>
                            <div className="column-sixty-five"></div>
                        </div>
                    </div>
                );

        }

    };

    return (
        <Col
            className="component-wrapper__col"
            key={`${component.ComponentId}-${index}`}
            lg={isSubReport ? 12 : component.WidthLg}
            md={isSubReport ? 12 : component.WidthMd}
            sm={isSubReport ? 12 : component.WidthSm}
            xl={isSubReport ? 12 : component.WidthXl}
            xxl={component.WidthXl}
        >
            {component.Results && !loading ? (
                <Container className="chart-generic">
                    {centerTitle && (
                    <h6 className="text-center chart-generic__title">
                        {centerTitle}
                    </h6>
                    )}
                    {component.Results?.Title && !centerTitle && (
                        <h6 className="text-center chart-generic__title">
                            {component.Results?.Title}
                            {' '}
                        </h6>
                    )}
                    <Chart donutCenterRender={centerRenderer}>
                        {component.Legend && (
                            <ChartLegend
                                orientation={component.Legend?.Orientation}
                                position={component.Legend?.Position}
                                visible={chartLegendVisible}
                            />
                        )}
                        <ChartTooltip render={renderTooltip} />
                        <ChartSeries>
                            {component.Results?.SeriesList.length === 0 && (
                            <div className="text-center">
                                <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                            </div>
                            )}

                            {component.Results &&
								component.Results?.SeriesList.map((dataItem, index) => {

								    if (dataItem.Data !== null) {

								        return (
    <ChartSeriesItem
        color={dataItem.Color}
        colorField="Color"
        data={dataItem.Data ? dataItem.Data : []}
        field="Share"
        key={index}
        name={dataItem.Name}
        sizeField="Share"
        type={dataItem.Type as SeriesType}
        {...dataItem.ExtendedProperties}
    />
								        );

								    }

								})}
                        </ChartSeries>
                        {component.Results && (component.Results.XTitle || component.Results.XAxisType) && (
                            <ChartXAxis>
                                <ChartXAxisItem
                                    title={{ text: component.Results.XTitle ?? '' }}
                                    type={component.Results.XAxisType ?? 'numeric'}
                                />
                            </ChartXAxis>
                        )}
                        {component.Results && (component.Results.YTitle || component.Results.YAxisType) && (
                            <ChartYAxis>
                                <ChartYAxisItem
                                    title={{ text: component.Results.YTitle ?? '' }}
                                    type={component.Results.YAxisType ?? 'numeric'}
                                />
                            </ChartYAxis>
                        )}
                        {component.Results && component.Results.Categories && (
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem
                                    {...(component.Results?.Categories
                                        ? { ...{ visible: true } }
                                        : { ...{ visible: false } })}
                                    categories={component.Results?.Categories}
                                />
                            </ChartCategoryAxis>
                        )}
                    </Chart>
                </Container>
            ) : (
                <>
                    {generateSkeletonChart(component)}
                </>
            )}
        </Col>
    );

};
